import React, { useState, useEffect, useCallback } from 'react';
import {
  LogOut, ExternalLink, Clock, CheckCircle, AlertTriangle, Send, Save, Lock,
  FileText, Ruler, Film, GraduationCap, Mail, Shield, Plus, Star, Trash2, RefreshCw,
} from 'lucide-react';
import { authFetch, API } from '../api';
import { input, Field, Card, STATUS } from './AthleteApp';
import { POSITION_GROUPS } from '../positions';

/*
 * The guardian's whole product: one page that edits their athlete's profile,
 * shows what the school has verified (and therefore locked), and submits for
 * review. Film is link-based here -- YouTube and Vimeo embed, and we do not
 * want families uploading raw video to this host.
 */

const VERIFIABLE_HELP =
  'Verified by the athlete’s program. Ask the coach to change it, or request a change below.';

const AthleteDashboard = ({ user, onLogout }) => {
  const [profiles, setProfiles] = useState([]);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({});
  const [baseline, setBaseline] = useState(null);
  const [videos, setVideos] = useState([]);
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const [attest, setAttest] = useState({ name: '', agreed: false });
  const [newVideo, setNewVideo] = useState({ title: '', url: '', provider: 'youtube', description: '' });
  // Which measurables to ask for depends on the position -- a tackle is judged
  // on length, a corner on speed. Driven by the server so it can change
  // without a deploy.
  const [measurables, setMeasurables] = useState([]);

  const flash = (m) => { setStatus(m); setTimeout(() => setStatus(''), 2800); };

  const load = useCallback(async () => {
    const r = await authFetch(`${API}/api/athlete/me`);
    if (!r.ok) return;
    const list = await r.json();
    setProfiles(list);
    if (list.length && active === null) setActive(list[0].id);
  }, [active]);

  useEffect(() => { load(); }, [load]);

  const current = profiles.find((p) => p.id === active) || null;

  const loadDetail = useCallback(async () => {
    if (!active) return;
    const share = await authFetch(`${API}/api/share/${active}/profile`);
    if (share.ok) {
      const d = await share.json();
      const asForm = {};
      Object.entries(d).forEach(([k, v]) => { asForm[k] = v ?? ''; });
      setForm(asForm); setBaseline(asForm);
    }
    const v = await authFetch(`${API}/api/share/${active}/videos`);
    if (v.ok) setVideos(await v.json());
  }, [active]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  const position = form.position || current?.position;
  useEffect(() => {
    if (!position) return;
    let cancelled = false;
    fetch(`${API}/api/athlete/measurables/${encodeURIComponent(position)}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => { if (!cancelled) setMeasurables(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [position]);

  const locked = new Set(current?.verified_fields || []);
  const isLocked = (f) => current?.claimed_by_school && locked.has(f);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!baseline) return;
    setBusy(true);
    try {
      const changed = {};
      Object.entries(form).forEach(([k, v]) => {
        if (String(v ?? '') !== String(baseline[k] ?? '')) changed[k] = v === '' ? null : v;
      });
      if (!Object.keys(changed).length) { flash('Nothing changed'); return; }
      const r = await authFetch(`${API}/api/athlete/profile/${active}`, {
        method: 'PATCH',
        body: JSON.stringify({ fields: changed }),
      });
      const d = await r.json();
      await loadDetail(); await load();
      flash(d.locked?.length
        ? `Saved. ${d.locked.length} field(s) are locked by the school.`
        : 'Saved ✓');
    } finally { setBusy(false); }
  };

  const submit = async () => {
    if (!attest.agreed || attest.name.trim().length < 2) {
      return flash('Confirm you are the parent or guardian, and type your name.');
    }
    setBusy(true);
    try {
      const r = await authFetch(`${API}/api/athlete/profile/${active}/submit`, {
        method: 'POST',
        body: JSON.stringify({ attested_name: attest.name, attests_guardian: true }),
      });
      if (!r.ok) throw new Error((await r.json()).detail);
      await load();
      flash('Submitted for review ✓');
    } catch (e) { flash(String(e.message || e)); }
    finally { setBusy(false); }
  };

  const addVideo = async () => {
    if (!newVideo.title.trim() || !newVideo.url.trim()) return flash('Title and link required');
    setBusy(true);
    try {
      await authFetch(`${API}/api/share/${active}/videos`, {
        method: 'POST',
        body: JSON.stringify({ ...newVideo, display_order: videos.length, is_featured: videos.length === 0 }),
      });
      setNewVideo({ title: '', url: '', provider: 'youtube', description: '' });
      await loadDetail(); flash('Film added ✓');
    } finally { setBusy(false); }
  };

  const removeVideo = async (v) => {
    if (!window.confirm(`Remove "${v.title}"?`)) return;
    await authFetch(`${API}/api/share/${active}/videos/${v.id}`, { method: 'DELETE' });
    loadDetail();
  };

  const requestChange = async (fieldName) => {
    const value = window.prompt(`Request a change to ${fieldName}. New value:`);
    if (!value) return;
    await authFetch(`${API}/api/athlete/profile/${active}/change-request`, {
      method: 'POST',
      body: JSON.stringify({ field_name: fieldName, requested_value: value }),
    });
    flash('Change requested — the school will review it.');
  };

  if (!current) {
    return (
      <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center px-4">
        <div className="text-[#00D4FF]">Loading…</div>
      </div>
    );
  }

  const st = STATUS[current.listing_status] || STATUS.draft;
  const canEdit = ['draft', 'changes_requested', 'live'].includes(current.listing_status);
  const canSubmit = ['draft', 'changes_requested'].includes(current.listing_status);

  const lockable = (name, label, type = 'text', extra = {}, hint = null) => (
    <Field label={label} locked={isLocked(name)}
      hint={isLocked(name) ? VERIFIABLE_HELP : hint}>
      <div className="flex gap-2">
        <input {...extra} type={type} className={input} value={form[name] ?? ''}
          disabled={isLocked(name)}
          style={isLocked(name) ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
          onChange={(e) => set(name, e.target.value)} />
        {isLocked(name) && (
          <button onClick={() => requestChange(name)} type="button"
            className="text-xs font-semibold text-[#00D4FF] border border-[#00D4FF]/30 rounded-lg px-2.5 flex-shrink-0">
            Request
          </button>
        )}
      </div>
    </Field>
  );

  return (
    <div className="min-h-screen bg-[#0A0E27] has-tab-bar">
      <header className="app-bar app-chrome">
        <div className="max-w-3xl mx-auto flex items-center justify-between h-14 px-4">
          <div className="flex items-center gap-2 min-w-0">
            <img src="/SchemeSync_Logo.png" alt="" className="h-7 w-auto flex-shrink-0" />
            <span className="text-sm font-bold text-white truncate">{current.name}</span>
          </div>
          <button onClick={onLogout} className="text-[#546E7A] p-2" aria-label="Sign out">
            <LogOut size={18} />
          </button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-5">
        {status && <div className="text-[#00E676] text-sm font-semibold mb-3">{status}</div>}

        {/* Status */}
        <div className="panel-card mb-4">
          <div className="flex items-center gap-2.5 mb-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: st.color }} />
            <span className="font-bold text-sm" style={{ color: st.color }}>{st.label}</span>
          </div>
          <p className="text-[#B0BEC5] text-xs">{st.help}</p>

          {current.review_note && (
            <div className="mt-3 rounded-lg border border-[#EF5350]/40 bg-[#EF5350]/[0.07] px-3 py-2.5">
              <div className="text-[#EF5350] text-xs font-bold mb-1">Note from review</div>
              <div className="text-[#C8D8E8] text-sm">{current.review_note}</div>
            </div>
          )}

          {current.public_url && (
            <a href={current.public_url} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 mt-3 text-sm font-semibold">
              <ExternalLink size={14} /> View your live page
            </a>
          )}

          {current.claimed_by_school && (
            <div className="flex items-start gap-2 mt-3 rounded-lg border border-[#00E676]/30 bg-[#00E676]/[0.06] px-3 py-2.5">
              <Shield size={14} className="text-[#00E676] flex-shrink-0 mt-0.5" />
              <p className="text-[#B0BEC5] text-xs">
                <strong className="text-white">{current.claimed_by_school}</strong> has verified
                this profile. Verified details are locked and shown to college coaches as
                program-verified.
              </p>
            </div>
          )}
        </div>

        {canEdit && (
          <>
            <Card icon={FileText} title="About" subtitle="What a coach reads first.">
              <div className="space-y-3.5">
                <Field label="Headline">
                  <input className={input} maxLength={300} value={form.public_headline ?? ''}
                    onChange={(e) => set('public_headline', e.target.value)} />
                </Field>
                <Field label="About">
                  <textarea rows={5} className={input} value={form.public_bio ?? ''}
                    onChange={(e) => set('public_bio', e.target.value)} />
                </Field>
                <Field label="Position" locked={isLocked('position')}
                  hint={isLocked('position') ? VERIFIABLE_HELP : 'Decides which measurables we ask for.'}>
                  <div className="flex gap-2">
                    <select className={input} value={form.position ?? ''}
                      disabled={isLocked('position')}
                      style={isLocked('position') ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
                      onChange={(e) => set('position', e.target.value)}>
                      <option value="">Choose a position…</option>
                      {POSITION_GROUPS.map((g) => (
                        <optgroup key={g.group} label={g.group}>
                          {g.positions.map((p) => (
                            <option key={p.code} value={p.code}>{p.label}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    {isLocked('position') && (
                      <button onClick={() => requestChange('position')} type="button"
                        className="text-xs font-semibold text-[#00D4FF] border border-[#00D4FF]/30 rounded-lg px-2.5 flex-shrink-0">
                        Request
                      </button>
                    )}
                  </div>
                </Field>
                <Field label="Projects at" hint="Widens the coaches who consider him.">
                  <input className={input} value={form.projected_positions ?? ''}
                    onChange={(e) => set('projected_positions', e.target.value)} />
                </Field>
              </div>
            </Card>

            <Card icon={Ruler} title="Measurables"
              subtitle={position
                ? `The numbers college coaches screen on for ${position}. Honest ones travel further than optimistic ones.`
                : 'Honest numbers travel further than optimistic ones.'}>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
                {measurables.map((m) => (
                  <div key={m.field_key} className={m.help_text ? 'sm:col-span-3' : ''}>
                    {lockable(
                      m.field_key,
                      m.unit ? `${m.display_name} (${m.unit})` : m.display_name,
                      m.input_type === 'text' ? 'text' : 'number',
                      {
                        step: m.step || undefined,
                        inputMode: m.input_type === 'text' ? undefined : 'decimal',
                        placeholder: m.placeholder || undefined,
                      },
                      m.help_text
                    )}
                  </div>
                ))}
              </div>
              {measurables.length === 0 && (
                <p className="text-[#546E7A] text-sm">
                  Set a position above and the measurables for it will appear here.
                </p>
              )}
              <div className="mt-3.5">
                <Field label="How were these measured?">
                  <select className={input} value={form.measurables_source ?? 'self_reported'}
                    onChange={(e) => set('measurables_source', e.target.value)}>
                    <option value="self_reported">Self-reported / family measured</option>
                    <option value="school_staff">Measured by school staff</option>
                    <option value="camp">Camp verified</option>
                    <option value="combine">Combine verified</option>
                  </select>
                </Field>
              </div>
            </Card>

            <Card icon={GraduationCap} title="Academics" subtitle="Coaches filter on eligibility early.">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
                {lockable('gpa', 'GPA', 'number')}
                {lockable('sat_score', 'SAT', 'number')}
                {lockable('act_score', 'ACT', 'number')}
                {lockable('class_year', 'Class year')}
                {lockable('graduation_year', 'Graduation year', 'number')}
                {lockable('high_school', 'High school')}
              </div>
            </Card>

            <Card icon={Film} title="Film"
              subtitle="Paste YouTube or Vimeo links — they play right on the page. Hudl links open on Hudl.">
              {videos.length > 0 && (
                <div className="space-y-2 mb-4">
                  {videos.map((v) => (
                    <div key={v.id} className="flex items-center gap-2 bg-[#0A1628] border border-[#546E7A]/25 rounded-lg px-3 py-2.5">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          {v.is_featured && <Star size={12} className="text-[#FFB300]" fill="#FFB300" />}
                          <span className="text-white text-sm font-medium truncate">{v.title}</span>
                        </div>
                        <div className="text-[#546E7A] text-xs truncate">{v.url}</div>
                      </div>
                      <button onClick={() => removeVideo(v)} className="p-2 text-[#546E7A]">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="space-y-3.5 border-t border-[#546E7A]/20 pt-4">
                <Field label="Clip title" hint="“Pass pro vs. #55, Week 3” beats “Highlights”.">
                  <input className={input} value={newVideo.title}
                    onChange={(e) => setNewVideo({ ...newVideo, title: e.target.value })} />
                </Field>
                <Field label="Link">
                  <input className={input} type="url" placeholder="https://youtube.com/watch?v=..."
                    value={newVideo.url}
                    onChange={(e) => setNewVideo({ ...newVideo, url: e.target.value })} />
                </Field>
                <button onClick={addVideo} disabled={busy}
                  className="btn-secondary w-full flex items-center justify-center gap-2 disabled:opacity-50">
                  <Plus size={16} /> Add film
                </button>
              </div>
              <Field label="Hudl profile URL">
                <input className={input} type="url" value={form.hudl_url ?? ''}
                  onChange={(e) => set('hudl_url', e.target.value)} />
              </Field>
            </Card>

            <Card icon={Mail} title="Contact"
              subtitle="Coach and parent only — never the athlete's own number.">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                <Field label="Coach name">
                  <input className={input} value={form.coach_name ?? ''}
                    onChange={(e) => set('coach_name', e.target.value)} />
                </Field>
                <Field label="Coach email">
                  <input className={input} type="email" value={form.coach_email ?? ''}
                    onChange={(e) => set('coach_email', e.target.value)} />
                </Field>
                <Field label="Your name">
                  <input className={input} value={form.guardian_name ?? ''}
                    onChange={(e) => set('guardian_name', e.target.value)} />
                </Field>
                <Field label="Your phone">
                  <input className={input} type="tel" value={form.guardian_phone ?? ''}
                    onChange={(e) => set('guardian_phone', e.target.value)} />
                </Field>
              </div>
            </Card>

            <button onClick={save} disabled={busy}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 mb-4">
              <Save size={16} /> {busy ? 'Saving…' : 'Save changes'}
            </button>
          </>
        )}

        {canSubmit && (
          <Card icon={Send} title="Submit for review"
            subtitle="We'll review it, send an invoice, and publish once payment is received.">
            <label className="flex items-start gap-2.5 cursor-pointer mb-3.5">
              <input type="checkbox" className="w-4 h-4 mt-0.5 accent-[#00D4FF] flex-shrink-0"
                checked={attest.agreed}
                onChange={(e) => setAttest({ ...attest, agreed: e.target.checked })} />
              <span className="text-[#B0BEC5] text-sm">
                I am the parent or legal guardian of this athlete, and I authorize this
                profile to be published publicly.
              </span>
            </label>
            <Field label="Type your full name">
              <input className={input} value={attest.name}
                onChange={(e) => setAttest({ ...attest, name: e.target.value })} />
            </Field>
            <button onClick={submit} disabled={busy}
              className="btn-primary w-full flex items-center justify-center gap-2 mt-3.5 disabled:opacity-50">
              <Send size={16} /> Submit for review
            </button>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AthleteDashboard;
